import { useState, useRef, useEffect } from "react";

export const ORANGE = "#e8773a";
export const GREEN = "#1e8a42";
export const RED = "#e05252";
export const GRAY = "#999";

export function fmtInt(n) {
  if (n == null) return "—";
  return Math.abs(n).toLocaleString("pt-BR");
}

export function fmtBRL(n) {
  if (n == null) return "—";
  return "R$ " + Math.abs(n).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function fmtBRLFull(n) {
  if (n == null) return "—";
  return "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function fmtBRLCompact(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1000000) return sign + "R$ " + (abs / 1000000).toFixed(1) + "M";
  if (abs >= 1000) return sign + "R$ " + (abs / 1000).toFixed(0) + "k";
  return sign + "R$ " + abs.toLocaleString("pt-BR");
}

export function fmtPct(n) {
  if (n == null) return "—";
  return n.toFixed(1) + "%";
}

export function numColor(n) {
  if (n == null) return "#333";
  return n >= 0 ? GREEN : RED;
}

export function cellBg(n) {
  if (n == null || n === 0) return "transparent";
  const abs = Math.abs(n);
  if (n > 0) {
    if (abs <= 5000) return "rgba(30,138,66,0.06)";
    if (abs <= 10000) return "rgba(30,138,66,0.13)";
    return "rgba(30,138,66,0.22)";
  }
  if (abs <= 5000) return "rgba(224,82,82,0.06)";
  if (abs <= 10000) return "rgba(224,82,82,0.13)";
  return "rgba(224,82,82,0.22)";
}

export function cellBgPerda(n) {
  if (!n) return "transparent";
  if (n <= 350000) return "rgba(217,119,6,0.07)";
  if (n <= 500000) return "rgba(217,119,6,0.14)";
  return "rgba(217,119,6,0.22)";
}

export function cellBgDark(n) {
  if (n == null || n === 0) return "rgba(0,0,0,0.05)";
  const abs = Math.abs(n);
  if (n > 0) {
    if (abs <= 5000) return "rgba(30,138,66,0.14)";
    if (abs <= 10000) return "rgba(30,138,66,0.22)";
    return "rgba(30,138,66,0.32)";
  }
  if (abs <= 5000) return "rgba(224,82,82,0.14)";
  if (abs <= 10000) return "rgba(224,82,82,0.22)";
  return "rgba(224,82,82,0.32)";
}

export function cellBgPerdaDark(n) {
  if (!n) return "rgba(0,0,0,0.05)";
  if (n <= 350000) return "rgba(217,119,6,0.14)";
  if (n <= 500000) return "rgba(217,119,6,0.22)";
  return "rgba(217,119,6,0.32)";
}

export function OtbCard({ children, style = {} }) {
  return (
    <div style={{
      backgroundColor: "#fff",
      border: "1px solid #e5e5e5",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      ...style,
    }}>
      {children}
    </div>
  );
}

export function OtbTab({ tabs, active, onSelect }) {
  return (
    <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e0e0e0" }}>
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onSelect(tab.key)}
            style={{
              padding: "7px 14px",
              fontSize: "12.5px",
              fontWeight: isActive ? "700" : "500",
              cursor: "pointer",
              background: isActive ? "#fff" : "#f7f7f7",
              border: isActive ? `1.5px solid ${ORANGE}` : "1px solid #e0e0e0",
              borderBottom: isActive ? "1.5px solid #fff" : "1px solid #e0e0e0",
              borderRadius: "6px 6px 0 0",
              color: isActive ? "#1a1a1a" : "#999",
              marginBottom: "-1px",
              outline: "none",
              whiteSpace: "nowrap",
              transition: "color 0.15s",
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function OtbSelect({ value, onChange, options, minWidth = 160, grayFill = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth, display: "inline-block" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 14px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          background: grayFill ? "#f5f5f5" : "#fff",
          cursor: "pointer",
          fontSize: "14px",
          color: "#333",
          outline: "none",
          whiteSpace: "nowrap",
        }}
      >
        <span>{value}</span>
        <svg
          width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          minWidth: "max(100%, 200px)",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          zIndex: 9999,
          maxHeight: 280,
          overflowY: "auto",
          padding: "4px 0",
        }}>
          {options.map((opt) => {
            const isActive = opt === value;
            return (
              <button
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 16px",
                  background: isActive ? "#fafafa" : "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13.5px",
                  color: "#1a1a1a",
                  fontWeight: isActive ? "600" : "400",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ width: 16, flexShrink: 0, color: ORANGE, fontWeight: "700", fontSize: "14px" }}>
                  {isActive ? "✓" : ""}
                </span>
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TableScrollX({ children }) {
  return (
    <div style={{ overflowX: "auto", width: "100%" }}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: "15px", fontWeight: "700", color: "#1a1a1a" }}>{children}</div>
      {sub && <div style={{ fontSize: "12px", color: GRAY, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
